import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, computed, effect, inject, output, signal, untracked, viewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { IconComponent } from '@kin/spark-ui';
import HelloSign from 'hellosign-embedded';
import { environment } from '../../../environments/environment';
import { SignupEnrollmentService } from './signup-enrollment.service';
import { SignupLoaderComponent } from '../ui/signup-loader.component';

/**
 * Form 50-162 signature field: nothing but the Dropbox Sign embed, opened the same way /sign opens it.
 * Until the SDK reports `ready`, the page's loader holds its place.
 * Sign form asks the enroll form to check its required fields and generate it (see SignupEnrollmentService).
 */
@Component({
  selector: 'app-signature-pad',
  imports: [IconComponent, DatePipe, SignupLoaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (signing.isSigned()) {
      <div class="signed" role="status">
        <ngk-icon icon="file-check" size="20px" />
        <span>Form 50-162 signed</span>
        <span class="signed-at">{{ signing.signedAt() | date: 'MMM d, y, h:mm a' }}</span>
      </div>
    } @else {
      <div class="frame" [class.open]="sdkOpen()" [class.invalid]="!!signing.error()">
        @if (sdkOpen()) {
          <div #signingContainer class="signing-container" [class.loading]="!sdkReady()"></div>
        }
        @if (loadingLabel(); as label) {
          <app-signup-loader class="loader">{{ label }}</app-signup-loader>
        } @else {
          @switch (signing.state()) {
            @case ('idle') {
              <div class="placeholder">
                <button type="button" class="sign" (click)="signRequested.emit()">
                  <ngk-icon icon="file-pen-line" size="20px" />Sign form
                </button>
                <p class="hint">Your Form 50-162 opens here once your details are filled in</p>
              </div>
            }
            @case ('error') {
              <div class="placeholder">
                <button type="button" class="sign" (click)="signRequested.emit()">
                  <ngk-icon icon="file-pen-line" size="20px" />Sign form
                </button>
              </div>
            }
            @case ('closed') {
              <button type="button" class="reopen" (click)="signing.reopen()">
                <ngk-icon icon="file-pen-line" size="20px" />Reopen Form 50-162
              </button>
            }
            @case ('login-required') {
              <div class="placeholder">
                <button type="button" class="sign" (click)="signRequested.emit()">
                  <ngk-icon icon="file-pen-line" size="20px" />Sign form
                </button>
                <p class="hint" role="status">
                  <ngk-icon icon="user" size="16px" />
                  <span>You already have a Kin account. Please contact us at <a href="mailto:propertytax@kin.com">propertytax@kin.com</a> if you have any questions.</span>
                </p>
              </div>
            }
          }
        }
      </div>
    }
    @if (signing.error()) {
      <p class="field-error" role="alert">{{ signing.error() }}</p>
    }
  `,
  styles: `
    :host { display: block; margin: 24px 0 20px; }
    .frame {
      position: relative; min-height: 120px; display: flex; align-items: center; justify-content: center;
      border-radius: var(--kin-radius-md);
    }
    .frame.invalid { outline: 1px solid var(--kin-red-600); }
    /* SDK open: sized like /sign's .signing-container (700px, 500px on mobile) */
    .frame.open { display: block; min-height: 700px; }
    .signing-container { width: 100%; }
    .signing-container.loading { visibility: hidden; }
    :host ::ng-deep .signing-container .x-hellosign-embedded__iframe {
      display: block; width: 100% !important; height: 700px !important;
      border: 1px solid var(--kin-border-subtle); border-radius: var(--kin-radius-md);
    }
    .frame.open .loader { position: absolute; inset: 0; }
    /* Equal 24px inset on every side; Neutral-600 dashes clear 3:1 against white (4.6:1) */
    .placeholder {
      width: 100%; box-sizing: border-box; padding: 24px; display: flex; flex-direction: column;
      align-items: center; gap: 12px; text-align: center;
      border: 1px dashed var(--kin-neutral-600); border-radius: var(--kin-radius-md);
    }
    .hint {
      display: flex; align-items: center; gap: 8px; margin: 0;
      font-size: 14px; line-height: 1.5; color: var(--kin-text-tertiary);
    }
    .hint a { color: var(--kin-blue-600); }
    .sign {
      display: inline-flex; align-items: center; gap: 10px; padding: 12px 24px; font: inherit; font-size: 16px;
      font-weight: 600; color: var(--kin-neutral-1000); background: var(--kin-yellow-600); cursor: pointer;
      border: 0; border-radius: var(--kin-radius-pill); transition: background 120ms var(--kin-ease-out);
    }
    .sign:hover { background: var(--kin-yellow-400); }
    .sign:active { background: var(--kin-yellow-700); }
    .sign:focus-visible { outline: none; box-shadow: var(--kin-shadow-focus); }
    @media (prefers-reduced-motion: reduce) { .sign { transition: none; } }
    .reopen {
      display: inline-flex; align-items: center; gap: 10px; padding: 10px 16px; font: inherit; font-size: 14px;
      font-weight: 600; color: var(--kin-text); background: var(--kin-white); cursor: pointer;
      border: 1px solid var(--kin-neutral-300); border-radius: var(--kin-radius-pill);
    }
    .reopen:hover { border-color: var(--kin-neutral-500); }
    .reopen:focus-visible { outline: none; box-shadow: var(--kin-shadow-focus); }
    /* Signed is one field done, not the form: neutral and bold (Neutral-1000 on white, 2px rule), no green success cue */
    .signed {
      display: flex; align-items: center; flex-wrap: wrap; gap: 4px 10px; padding: 12px 16px;
      font-size: 14px; font-weight: 700; color: var(--kin-text); background: var(--kin-white);
      border: 2px solid var(--kin-neutral-1000); border-radius: var(--kin-radius-md);
    }
    .signed-at { margin-left: auto; font-weight: 500; color: var(--kin-text-secondary); }
    .field-error { color: var(--kin-red-600); font-size: 12px; font-weight: 500; margin: 6px 0 0; }
    @media (max-width: 600px) {
      .frame.open { min-height: 500px; }
      :host ::ng-deep .signing-container .x-hellosign-embedded__iframe { height: 500px !important; }
    }
  `,
})
export class SignaturePadComponent {
  protected readonly signing = inject(SignupEnrollmentService);
  private readonly container = viewChild<ElementRef<HTMLDivElement>>('signingContainer');

  private client: HelloSign | null = null;

  /** Sign form: the enroll form checks the required fields, then starts the request. */
  readonly signRequested = output<void>();

  protected readonly sdkOpen = computed(() => this.signing.state() === 'signing');
  /** The SDK's `ready` event: the document has loaded in the iframe, so it replaces the loader. */
  protected readonly sdkReady = signal(false);
  /** Shown by the page loader until the SDK replaces it; empty when nothing is loading. */
  protected readonly loadingLabel = computed(() => {
    if (this.sdkOpen()) return this.sdkReady() ? '' : 'Loading Form 50-162…';
    return this.signing.state() === 'preparing' ? this.signing.preparingLabel() : '';
  });

  constructor() {
    // Opens the SDK once its container renders for an open request; closes it when signing settles
    effect(() => {
      const el = this.container()?.nativeElement;
      const request = this.signing.request();
      if (!this.sdkOpen() || !el || !request?.signingUrl) {
        untracked(() => this.close());
        return;
      }
      untracked(() => this.open(request.signingUrl!, request.clientId, el));
    });
    inject(DestroyRef).onDestroy(() => this.close());
  }

  /** Same embed and event handling as SignComponent.openHelloSign; signals carry the updates (no NgZone.run). */
  private open(signingUrl: string, clientId: string, container: HTMLElement): void {
    if (this.client) return;
    this.client = new HelloSign({ clientId });
    this.client.on('ready', () => this.sdkReady.set(true));
    this.client.on('sign', () => this.signing.markSigned());
    this.client.on('finish', () => this.signing.markSigned());
    this.client.on('decline', () => this.signing.decline());
    this.client.on('error', () => this.signing.fail('Something went wrong while signing. Please try again.'));
    this.client.on('cancel', () => this.signing.cancel());
    this.client.open(signingUrl, { container, skipDomainVerification: environment.skipDomainVerification ?? false });
  }

  private close(): void {
    this.client?.close();
    this.client = null;
    this.sdkReady.set(false);
  }
}
